import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { listWorkers, toggleWorkerActive } from "../services/worker";
import { getUserInfo } from "../services/auth";

const PAGE_SIZE = 8;

const isWorkerActive = (worker) => String(worker.active) === "1";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" });
};

const ListaFuncionariosLayer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [togglingId, setTogglingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const fetchWorkers = () => {
    setLoading(true);
    listWorkers()
      .then((resp) => setWorkers(resp.data || []))
      .catch(() => setWorkers([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchWorkers();
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return workers.filter((w) => {
      const matchesTerm =
        !term ||
        w.name?.toLowerCase().includes(term) ||
        w.email?.toLowerCase().includes(term) ||
        w.contact?.toLowerCase().includes(term);
      const matchesStatus =
        !status || (status === "active" ? isWorkerActive(w) : !isWorkerActive(w));
      return matchesTerm && matchesStatus;
    });
  }, [workers, search, status]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const goToPage = (next) => {
    if (next < 1 || next > totalPages) return;
    setPage(next);
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const clearFilters = () => {
    setSearch("");
    setStatus("");
    setPage(1);
  };

  const toggleExpand = (id) => setExpandedId((cur) => (cur === id ? null : id));

  const handleToggleActive = async (worker) => {
    setTogglingId(worker.id);
    const nextActive = isWorkerActive(worker) ? 0 : 1;
    try {
      await toggleWorkerActive(worker.id, nextActive, getUserInfo()?.id);
      toast.success(
        nextActive === 1
          ? t("funcionario.list.activate.success")
          : t("funcionario.list.inactivate.success"),
      );
      fetchWorkers();
    } catch (err) {
      toast.error(t("funcionario.list.toggle.error"));
    } finally {
      setTogglingId(null);
    }
  };

  const handleEdit = (worker) => {
    navigate(`/funcionarios/editar/${worker.id}`, { state: { worker } });
  };

  const activeCount = workers.filter(isWorkerActive).length;
  const inactiveCount = workers.length - activeCount;

  return (
    <div className='row gy-4'>
      <ToastContainer />

      <div className='col-12'>
        <div className='d-flex flex-wrap align-items-center gap-3'>
          <div className='card flex-fill radius-12 border-0 shadow-sm'>
            <div className='card-body d-flex align-items-center gap-3 py-16 px-20'>
              <span className='w-44-px h-44-px radius-12 bg-primary-50 text-primary-600 d-flex align-items-center justify-content-center text-xl'>
                <Icon icon='solar:users-group-rounded-bold' />
              </span>
              <div>
                <h6 className='mb-0'>{workers.length}</h6>
                <span className='text-secondary-light text-sm'>
                  {t("funcionario.list.stat.total")}
                </span>
              </div>
            </div>
          </div>
          <div className='card flex-fill radius-12 border-0 shadow-sm'>
            <div className='card-body d-flex align-items-center gap-3 py-16 px-20'>
              <span className='w-44-px h-44-px radius-12 bg-success-focus text-success-main d-flex align-items-center justify-content-center text-xl'>
                <Icon icon='ic:outline-check-circle' />
              </span>
              <div>
                <h6 className='mb-0'>{activeCount}</h6>
                <span className='text-secondary-light text-sm'>
                  {t("funcionario.list.stat.active")}
                </span>
              </div>
            </div>
          </div>
          <div className='card flex-fill radius-12 border-0 shadow-sm'>
            <div className='card-body d-flex align-items-center gap-3 py-16 px-20'>
              <span className='w-44-px h-44-px radius-12 bg-danger-focus text-danger-main d-flex align-items-center justify-content-center text-xl'>
                <Icon icon='ic:outline-block' />
              </span>
              <div>
                <h6 className='mb-0'>{inactiveCount}</h6>
                <span className='text-secondary-light text-sm'>
                  {t("funcionario.list.stat.inactive")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='col-12'>
        <div className='card radius-12'>
          <div className='card-body'>
            <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24'>
              <div>
                <h6 className='mb-4'>{t("funcionario.list.title")}</h6>
                <p className='text-secondary-light mb-0 text-sm'>{t("funcionario.list.subtitle")}</p>
              </div>
              <button
                type='button'
                className='btn btn-primary-600 d-flex align-items-center gap-2'
                onClick={() => navigate("/funcionarios/cadastrar")}
              >
                <Icon icon='ic:baseline-plus' />
                {t("funcionario.list.add")}
              </button>
            </div>

            <div className='p-16 radius-8 bg-neutral-50 mb-24'>
              <div className='row gy-3 align-items-end'>
                <div className='col-sm-6 col-lg-5'>
                  <label className='form-label text-sm mb-8'>{t("global.name")}</label>
                  <div className='icon-field'>
                    <span className='icon top-50 translate-middle-y'>
                      <Icon icon='ion:search-outline' />
                    </span>
                    <input
                      type='text'
                      className='form-control'
                      placeholder={t("funcionario.list.search.placeholder")}
                      value={search}
                      onChange={handleSearch}
                    />
                  </div>
                </div>
                <div className='col-sm-6 col-lg-3'>
                  <label className='form-label text-sm mb-8'>{t("global.status")}</label>
                  <select
                    className='form-control form-select'
                    value={status}
                    onChange={(e) => {
                      setStatus(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value=''>{t("global.all")}</option>
                    <option value='active'>{t("global.active")}</option>
                    <option value='inactive'>{t("global.inactive")}</option>
                  </select>
                </div>
                <div className='col-lg-1'>
                  <button
                    type='button'
                    className='btn btn-outline-secondary w-100 d-flex align-items-center justify-content-center'
                    title={t("global.clean.fields")}
                    onClick={clearFilters}
                  >
                    <Icon icon='ic:round-refresh' />
                  </button>
                </div>
              </div>
            </div>

            <div className='table-responsive scroll-sm'>
              <table className='table bordered-table mb-0'>
                <thead>
                  <tr>
                    <th scope='col' style={{ width: 36 }} />
                    <th scope='col'>{t("global.name")}</th>
                    <th scope='col'>{t("global.email")}</th>
                    <th scope='col'>{t("global.contact")}</th>
                    <th scope='col'>{t("global.register.date")}</th>
                    <th scope='col'>{t("global.status")}</th>
                    <th scope='col' className='text-end'>
                      {t("global.col.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={7} className='text-center text-secondary-light py-32'>
                        {t("funcionario.list.loading")}
                      </td>
                    </tr>
                  )}
                  {!loading && pageItems.length === 0 && (
                    <tr>
                      <td colSpan={7} className='text-center text-secondary-light py-32'>
                        {t("funcionario.list.empty")}
                      </td>
                    </tr>
                  )}
                  {pageItems.map((worker) => {
                    const isExpanded = expandedId === worker.id;
                    const busy = togglingId === worker.id;
                    return (
                      <React.Fragment key={worker.id}>
                        <tr
                          role='button'
                          onClick={() => toggleExpand(worker.id)}
                          className={isExpanded ? "bg-neutral-50" : ""}
                        >
                          <td>
                            <Icon
                              icon={isExpanded ? "ic:round-expand-less" : "ic:round-expand-more"}
                              className='text-secondary-light text-xl'
                            />
                          </td>
                          <td className='fw-semibold text-primary-light'>{worker.name}</td>
                          <td>{worker.email || "-"}</td>
                          <td>{worker.contact || "-"}</td>
                          <td>{formatDate(worker.createdAt)}</td>
                          <td>
                            <span
                              className={`px-10 py-4 radius-8 fw-medium text-sm ${
                                isWorkerActive(worker)
                                  ? "bg-success-focus text-success-main"
                                  : "bg-danger-focus text-danger-main"
                              }`}
                            >
                              {isWorkerActive(worker) ? t("global.active") : t("global.inactive")}
                            </span>
                          </td>
                          <td className='text-end' onClick={(e) => e.stopPropagation()}>
                            <div className='d-flex align-items-center justify-content-end gap-3'>
                              <button
                                type='button'
                                className='btn btn-outline-primary-600 btn-sm d-flex align-items-center gap-1'
                                onClick={() => handleEdit(worker)}
                              >
                                <Icon icon='solar:pen-2-linear' /> {t("global.edit")}
                              </button>
                              <button
                                type='button'
                                className={`btn btn-sm d-flex align-items-center gap-1 ${
                                  isWorkerActive(worker) ? "btn-outline-danger-600" : "btn-outline-success-600"
                                }`}
                                disabled={busy}
                                onClick={() => handleToggleActive(worker)}
                              >
                                <Icon
                                  icon={
                                    isWorkerActive(worker) ? "ic:outline-block" : "ic:outline-check-circle"
                                  }
                                />
                                {isWorkerActive(worker)
                                  ? t("funcionario.list.inactivate")
                                  : t("funcionario.list.activate")}
                              </button>
                            </div>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={7} className='p-0'>
                              <div className='p-24 bg-neutral-25 border-top'>
                                <div className='row gy-3'>
                                  <div className='col-md-3 text-center'>
                                    <div className='w-160-px h-160-px radius-12 border overflow-hidden bg-neutral-100 d-flex align-items-center justify-content-center mx-auto shadow-sm'>
                                      <Icon
                                        icon='solar:user-rounded-bold'
                                        className='text-3xl text-neutral-400'
                                      />
                                    </div>
                                    <div className='mt-16 d-flex flex-column align-items-center gap-8'>
                                      <span
                                        className={`px-12 py-4 radius-8 fw-medium text-sm ${
                                          isWorkerActive(worker)
                                            ? "bg-success-focus text-success-main"
                                            : "bg-danger-focus text-danger-main"
                                        }`}
                                      >
                                        {(isWorkerActive(worker)
                                          ? t("global.active")
                                          : t("global.inactive")
                                        ).toUpperCase()}
                                      </span>
                                      {worker.contractType && (
                                        <span className='px-12 py-4 radius-8 fw-medium text-sm bg-primary-50 text-primary-600 d-flex align-items-center gap-1'>
                                          <Icon icon='solar:case-round-outline' />
                                          {worker.contractType}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className='col-md-9'>
                                    <ul
                                      className='nav nav-pills mb-16 gap-2'
                                      id={`funcionario-tabs-${worker.id}`}
                                      role='tablist'
                                    >
                                      <li className='nav-item' role='presentation'>
                                        <button
                                          className='nav-link active radius-8 text-sm'
                                          data-bs-toggle='pill'
                                          data-bs-target={`#personal-func-${worker.id}`}
                                          type='button'
                                          role='tab'
                                        >
                                          {t("funcionario.expand.tab.personal")}
                                        </button>
                                      </li>
                                      <li className='nav-item' role='presentation'>
                                        <button
                                          className='nav-link radius-8 text-sm'
                                          data-bs-toggle='pill'
                                          data-bs-target={`#degree-func-${worker.id}`}
                                          type='button'
                                          role='tab'
                                        >
                                          {t("funcionario.expand.tab.degree")}
                                        </button>
                                      </li>
                                      <li className='nav-item' role='presentation'>
                                        <button
                                          className='nav-link radius-8 text-sm'
                                          data-bs-toggle='pill'
                                          data-bs-target={`#contract-func-${worker.id}`}
                                          type='button'
                                          role='tab'
                                        >
                                          {t("funcionario.expand.tab.contract")}
                                        </button>
                                      </li>
                                    </ul>
                                    <div className='tab-content'>
                                      <div
                                        className='tab-pane fade show active'
                                        id={`personal-func-${worker.id}`}
                                        role='tabpanel'
                                      >
                                        <table className='table table-bordered table-sm mb-0 bg-base'>
                                          <tbody>
                                            <tr>
                                              <th className='w-25 bg-neutral-50'>
                                                {t("funcionario.field.name")}
                                              </th>
                                              <td colSpan={3}>
                                                <strong>{worker.name}</strong>
                                              </td>
                                            </tr>
                                            <tr>
                                              <th className='bg-neutral-50'>{t("global.bi")}</th>
                                              <td>{worker.idNumber || "-"}</td>
                                              <th className='bg-neutral-50'>{t("global.nuit")}</th>
                                              <td>{worker.taxId || worker.nuit || "-"}</td>
                                            </tr>
                                            <tr>
                                              <th className='bg-neutral-50'>
                                                {t("global.birth.date")}
                                              </th>
                                              <td>{formatDate(worker.birthDate)}</td>
                                              <th className='bg-neutral-50'>{t("global.gender")}</th>
                                              <td>{worker.gender || "-"}</td>
                                            </tr>
                                            <tr>
                                              <th className='bg-neutral-50'>
                                                {t("global.marital.status")}
                                              </th>
                                              <td>{worker.maritalStatus || "-"}</td>
                                              <th className='bg-neutral-50'>{t("global.contact")}</th>
                                              <td>{worker.contact || "-"}</td>
                                            </tr>
                                          </tbody>
                                        </table>
                                      </div>
                                      <div
                                        className='tab-pane fade'
                                        id={`degree-func-${worker.id}`}
                                        role='tabpanel'
                                      >
                                        <table className='table table-bordered table-sm mb-0 bg-base'>
                                          <tbody>
                                            <tr>
                                              <th className='w-25 bg-neutral-50'>
                                                {t("global.educational.level")}
                                              </th>
                                              <td colSpan={3}>{worker.educationLevel || "-"}</td>
                                            </tr>
                                            <tr>
                                              <th className='bg-neutral-50'>
                                                {t("funcionario.field.degreeYear")}
                                              </th>
                                              <td>{worker.lastEducationYear || "-"}</td>
                                              <th className='bg-neutral-50'>
                                                {t("funcionario.field.institution")}
                                              </th>
                                              <td>{worker.institution || "-"}</td>
                                            </tr>
                                          </tbody>
                                        </table>
                                      </div>
                                      <div
                                        className='tab-pane fade'
                                        id={`contract-func-${worker.id}`}
                                        role='tabpanel'
                                      >
                                        <table className='table table-bordered table-sm mb-0 bg-base'>
                                          <tbody>
                                            <tr>
                                              <th className='w-25 bg-neutral-50'>
                                                {t("funcionario.field.contractType")}
                                              </th>
                                              <td>{worker.contractType || "-"}</td>
                                              <th className='bg-neutral-50'>
                                                {t("global.contract.position")}
                                              </th>
                                              <td>{worker.position || "-"}</td>
                                            </tr>
                                            <tr>
                                              <th className='bg-neutral-50'>
                                                {t("global.register.date")}
                                              </th>
                                              <td>{formatDate(worker.createdAt)}</td>
                                              <th className='bg-neutral-50'>{t("global.status")}</th>
                                              <td>
                                                {isWorkerActive(worker)
                                                  ? t("global.active")
                                                  : t("global.inactive")}
                                              </td>
                                            </tr>
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mt-16'>
              <span className='text-secondary-light text-sm'>
                {filtered.length} {t("funcionario.list.total")}
              </span>
              {!loading && filtered.length > PAGE_SIZE && (
                <ul className='pagination d-flex flex-wrap align-items-center gap-2 mb-0'>
                  <li className='page-item'>
                    <button
                      type='button'
                      className='page-link text-secondary-light fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px w-32-px bg-base'
                      onClick={() => goToPage(page - 1)}
                      disabled={page === 1}
                    >
                      <Icon icon='ep:d-arrow-left' className='text-xl' />
                    </button>
                  </li>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <li key={p} className='page-item'>
                      <button
                        type='button'
                        className={`page-link fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px w-32-px ${
                          p === page ? "bg-primary-600 text-white" : "bg-primary-50 text-secondary-light"
                        }`}
                        onClick={() => goToPage(p)}
                      >
                        {p}
                      </button>
                    </li>
                  ))}
                  <li className='page-item'>
                    <button
                      type='button'
                      className='page-link text-secondary-light fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px w-32-px bg-base'
                      onClick={() => goToPage(page + 1)}
                      disabled={page === totalPages}
                    >
                      <Icon icon='ep:d-arrow-right' className='text-xl' />
                    </button>
                  </li>
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListaFuncionariosLayer;
